// IIFE (Immediately Invoked Function Expression) to avoid global namespace pollution
// This prevents duplicate class declaration errors when multiple instances are loaded
(function() {
  // Only define classes if they don't already exist
  if (!customElements.get('product-bundle-variant-item')) {
    class ProductBundleVariantSelector extends HTMLElement {
      constructor() {
        super();
        
        // Get all option selectors within this item
        this.optionSelectors = this.querySelectorAll('[data-option-selector]');
        
        // Get the variant selector (could be hidden select or simple input for products without variants)
        this.variantSelector = this.querySelector('.product-variant-selector') || this.querySelector('input[name="id"]');
        
        // Set up event listeners for option selectors (only if they exist)
        if (this.optionSelectors.length > 0) {
          this.optionSelectors.forEach(select => {
            select.addEventListener('change', this.onOptionChange.bind(this));
          });
          
          // Initialize the variant selection for products with variants
          this.updateVariantSelection();
        }
        
        // Initial update of bundle total price
        this.dispatchEvent(new CustomEvent('variant:change', {
          bubbles: true,
          detail: {
            variant: this.getCurrentVariant(),
            productId: this.getAttribute('data-select-id')?.replace('bundle_block_', '')
          }
        }));
      }
      
      // When an option changes
      onOptionChange(event) {
        // Add a visual indication that price is updating
        const priceContainer = this.querySelector('.price');
        if (priceContainer) {
          priceContainer.classList.add('price-updating');
          // Remove the class after animation completes
          setTimeout(() => {
            priceContainer.classList.remove('price-updating');
          }, 1000);
        }
        
        this.updateVariantSelection();
        
        // Trigger a custom event that parent components can listen for
        this.dispatchEvent(new CustomEvent('variant:change', {
          bubbles: true,
          detail: {
            variant: this.getCurrentVariant(),
            productId: this.getAttribute('data-select-id')?.replace('bundle_block_', '')
          }
        }));
      }
      
      // Get the currently selected values for all options
      getSelectedOptions() {
        if (this.optionSelectors.length === 0) {
          return []; // No options for products without variants
        }
        
        return Array.from(this.optionSelectors).map(select => {
          return select.value;
        });
      }
      
      // Update the hidden variant selector based on selected options
      updateVariantSelection() {
        // Skip if no option selectors (products without variants)
        if (this.optionSelectors.length === 0) {
          return;
        }
        
        const selectedOptions = this.getSelectedOptions();
        
        // Find the matching variant
        const matchingVariant = this.findMatchingVariant(selectedOptions);
        
        if (matchingVariant) {
          // Update the hidden variant selector
          this.variantSelector.value = matchingVariant.value;
          
          // Update price display - Fix for price update
          // Find all price elements within this product item
          const priceContainer = this.querySelector('.price');
          if (priceContainer) {
            // Regular price display
            const regularPriceElement = priceContainer.querySelector('.price__regular .price-item');
            // Sale price display
            const salePriceElement = priceContainer.querySelector('.price__sale .price-item--sale');
            // Compare at price display
            const compareAtPriceElement = priceContainer.querySelector('.price__sale .price-item--regular');
            
            // Check if there's a sale price (compare at price exists and is greater than regular price)
            const hasCompareAtPrice = matchingVariant.dataset.compareAtPrice && 
                                    parseFloat(matchingVariant.dataset.compareAtPrice.replace(/[^0-9.-]+/g, "")) > 
                                    parseFloat(matchingVariant.dataset.price.replace(/[^0-9.-]+/g, ""));
            
            if (hasCompareAtPrice) {
              // Show sale price
              if (priceContainer.querySelector('.price__regular')) {
                priceContainer.querySelector('.price__regular').style.display = 'none';
              }
              if (priceContainer.querySelector('.price__sale')) {
                priceContainer.querySelector('.price__sale').style.display = 'block';
              }
              if (salePriceElement) {
                salePriceElement.innerHTML = matchingVariant.dataset.price;
              }
              if (compareAtPriceElement) {
                compareAtPriceElement.innerHTML = matchingVariant.dataset.compareAtPrice;
              }
            } else {
              // Show regular price
              if (priceContainer.querySelector('.price__regular')) {
                priceContainer.querySelector('.price__regular').style.display = 'block';
              }
              if (priceContainer.querySelector('.price__sale')) {
                priceContainer.querySelector('.price__sale').style.display = 'none';
              }
              if (regularPriceElement) {
                regularPriceElement.innerHTML = matchingVariant.dataset.price;
              }
            }
          }
          
          // Update the product image if this variant has a specific image
          this.updateProductImage(matchingVariant);
          
          // If variant is not available, show message or handle accordingly
          if (matchingVariant.dataset.available === 'false') {
            this.classList.add('variant-unavailable');
            
            // Dispatch an event to notify the parent component that the variant is unavailable
            this.dispatchEvent(new CustomEvent('variant:unavailable', {
              bubbles: true,
              detail: {
                variantId: matchingVariant.value,
                productId: this.getAttribute('data-select-id')?.replace('bundle_block_', '')
              }
            }));
          } else {
            this.classList.remove('variant-unavailable');
          }
        }
      }
      
      // Update product image based on selected variant
      updateProductImage(variant) {
        // Check if the variant has a media ID
        const mediaId = variant.dataset.mediaId;
        if (!mediaId || mediaId === '') return;
        
        // Find the product ID from the data-select-id attribute
        const productId = this.getAttribute('data-select-id')?.replace('bundle_block_', '');
        if (!productId) return;
        
        // First, try to update image in the product card
        const productCardImage = this.querySelector('.product-bundle-card__image img');
        if (productCardImage) {
          // Find the matching media in the hidden media list
          const mediaElement = this.querySelector(`.product-media-list [data-media-id="${mediaId}"]`);
          if (mediaElement) {
            const newImage = mediaElement.querySelector('img');
            if (newImage) {
              // Add a temporary class for transition effect
              const imageContainer = this.querySelector('.product-bundle-card__image');
              if (imageContainer) {
                imageContainer.classList.add('image-switching');
                setTimeout(() => {
                  imageContainer.classList.remove('image-switching');
                }, 300); // Match with CSS transition time
              }
              
              // Update the image source, srcset and other attributes
              productCardImage.src = newImage.src;
              if (newImage.srcset) {
                productCardImage.srcset = newImage.srcset;
              }
              if (newImage.alt) {
                productCardImage.alt = newImage.alt;
              }
              
              // Update sold out badge visibility based on variant availability
              const soldOutBadge = this.querySelector('.product-bundle-card__variant-status');
              if (soldOutBadge) {
                if (variant.dataset.available === 'false') {
                  soldOutBadge.style.display = 'block';
                } else {
                  soldOutBadge.style.display = 'none';
                }
              }
              
              // Notify parent component about the image change (for hotspot image)
              this.dispatchEvent(new CustomEvent('variant:imagechange', {
                bubbles: true,
                detail: {
                  variantId: variant.value,
                  productId: productId,
                  mediaId: mediaId,
                  mediaSrc: newImage.src,
                  mediaSrcset: newImage.srcset
                }
              }));
            }
          }
        }
      }
      
      // Find a variant option that matches all selected options
      findMatchingVariant(selectedOptions) {
        // If no variant selector or it's not a select element, return null
        if (!this.variantSelector || this.variantSelector.tagName !== 'SELECT') {
          return null;
        }
        
        return Array.from(this.variantSelector.options).find(option => {
          return selectedOptions.every((selectedOption, index) => {
            return option.dataset[`option${index + 1}`] === selectedOption;
          });
        });
      }
      
      // Get the current selected variant - IMPROVED for handling products without variants
      getCurrentVariant() {
        if (!this.variantSelector) {
          return null;
        }
        
        // For products with variants (select element)
        if (this.variantSelector.tagName === 'SELECT') {
          const selectedVariantId = this.variantSelector.value;
          return Array.from(this.variantSelector.options).find(option => option.value === selectedVariantId);
        }
        
        // For products without variants (input element)
        if (this.variantSelector.tagName === 'INPUT') {
          // Create a mock variant object for consistency
          return {
            value: this.variantSelector.value,
            dataset: {
              price: this.variantSelector.dataset.price || '0',
              regularPrice: this.variantSelector.dataset.price || '0',
              available: 'true', // Default variants are usually available
              compareAtPrice: this.variantSelector.dataset.compareAtPrice || null
            }
          };
        }
        
        return null;
      }
      
      // Get variant ID for cart
      getVariantId() {
        if (!this.variantSelector) {
          return null;
        }
        
        return this.variantSelector.value;
      }
      
      // Check if product is available - IMPROVED with better detection
      isAvailable() {
        const variant = this.getCurrentVariant();
        if (!variant) return false;
        
        // For input elements (products without variants), check if they exist and have value
        if (this.variantSelector.tagName === 'INPUT') {
          return this.variantSelector.value && this.variantSelector.value !== '';
        }
        
        // For select elements (products with variants)
        if (this.variantSelector.tagName === 'SELECT') {
          // Check if the selected option exists and is available
          const selectedOption = this.variantSelector.options[this.variantSelector.selectedIndex];
          if (!selectedOption) return false;
          
          // Check data-available attribute
          return selectedOption.dataset.available !== 'false' && selectedOption.value !== '';
        }
        
        return false;
      }
      
      // Get product title for notifications
      getProductTitle() {
        const titleElement = this.querySelector('h3');
        return titleElement ? titleElement.textContent.trim() : 'Selected product';
      }
    }

    // Only define the custom element if it hasn't already been defined
    customElements.define('product-bundle-variant-item', ProductBundleVariantSelector);
  }

  // Only define the main ProductBundle class if it doesn't already exist
  if (!customElements.get('products-bundle-group')) {
    class ProductBundle extends HTMLElement {
      constructor() {
        super();
        this.wrap = this.querySelector(".products-bundle-group-wrap");
        this.cartNotification = document.querySelector("cart-notification");
        this.cartItems = document.querySelector("cart-items");
        this.addToCartButtonElement = this.querySelector(".bundle-product-submit-button");
        this.bundleTotalPrice = this.querySelector(".bundle-total-price");
        this.itemSelector = this.querySelectorAll("product-bundle-variant-item");
        this.productBundleListWrap = this.querySelector(".product-bundle__list");
        
        // Get the main bundle image element
        this.mainBundleImage = this.querySelector('.product-bundle__media img');
        
        if (this.addToCartButtonElement) {
          this.addToCartButtonElement.addEventListener(
            "click",
            this.handleButtonClick.bind(this)
          );
        }
        
        // Setup hover listeners initially
        this.setupHoverListeners();
        
        // Listen for variant changes to update total price and check availability
        this.addEventListener('variant:change', this.handleVariantChange.bind(this));
        
        // Listen for variant image changes to update hotspot image
        this.addEventListener('variant:imagechange', this.handleVariantImageChange.bind(this));
        
        // Initialize total price calculation and availability check
        this.updateTotalPrice();
        this.checkBundleAvailability();
        
        // Check if the bundle is empty on initialization
        this.checkEmptyBundle();
        
        // Theme editor integration with DOM mutation observer
        this.setupThemeEditorIntegration();
      }
      
      // Setup hover listeners
      setupHoverListeners() {
        // Remove old hover listeners first
        if (this.productBundleItemSelector) {
          this.productBundleItemSelector.forEach((block) => {
            const clonedBlock = block.cloneNode(true);
            if (block.parentNode) {
              block.parentNode.replaceChild(clonedBlock, block);
            }
          });
        }
        
        // Setup new hover listeners with fresh elements
        this.productBundleItemSelector = this.querySelectorAll("[data-select-id]");
        this.productBundleItemSelector.forEach((block) => {
          block.addEventListener("mouseenter", this.handleBlockHover.bind(this, "enter"));
          block.addEventListener("mouseleave", this.handleBlockHover.bind(this, "leave"));
        });
        
        console.log('Hover listeners updated for', this.productBundleItemSelector.length, 'elements');
      }
      
      // Theme editor integration with MutationObserver for DOM changes
      setupThemeEditorIntegration() {
        if (window.Shopify && window.Shopify.designMode) {
          
          // Simple function to activate a specific bundle item
          const activateBundleItem = (bundleItem) => {
            if (!bundleItem) return;
            
            // Add hover state like mouse hover
            this.classList.add("is-hover");
            this.toggleBlockActive(bundleItem, true);
          };
          
          // Simple function to deactivate bundle items
          const deactivateBundleItems = () => {
            this.classList.remove("is-hover");
            this.toggleBlockActive(null, false);
          };
          
          // Store the current handler function
          this.themeEditorSelectHandler = (event) => {
            const blockElement = event.target;
            
            // Check if block belongs to our bundle section
            if (blockElement && this.contains(blockElement)) {
              const blockId = event.detail?.blockId;
              console.log('🎯 Block select - Block ID:', blockId);
              
              if (blockId) {
                // Always query fresh from current DOM
                const bundleItem = this.querySelector(`[data-select-id="bundle_block_${blockId}"]`);
                const hotspotButton = this.querySelector(`.product-bundle-pin_item[data-select-id="bundle_block_${blockId}"]`);
                
                console.log('📍 Found bundle item:', bundleItem);
                console.log('📍 Found hotspot button:', hotspotButton);
                
                if (bundleItem || hotspotButton) {
                  activateBundleItem(bundleItem || hotspotButton);
                  console.log('✅ Bundle item activated successfully');
                } else {
                  console.log('❌ No matching elements found for block ID:', blockId);
                  
                  // Debug: Show all available data-select-id values
                  const allElements = this.querySelectorAll('[data-select-id]');
                  console.log('🔍 Available elements with data-select-id:');
                  allElements.forEach(el => {
                    console.log('   -', el.getAttribute('data-select-id'), el);
                  });
                }
              }
            }
          };
          
          this.themeEditorDeselectHandler = (event) => {
            const blockElement = event.target;
            
            // Check if block belongs to our bundle section
            if (blockElement && this.contains(blockElement)) {
              deactivateBundleItems();
            }
          };
          
          // Add theme editor event listeners
          document.addEventListener('shopify:block:select', this.themeEditorSelectHandler);
          document.addEventListener('shopify:block:deselect', this.themeEditorDeselectHandler);
          
          // Setup MutationObserver to watch for DOM changes
          this.setupDOMObserver();
          
          console.log('🎭 Theme editor integration initialized with DOM observer');
        }
      }
      
      // Setup MutationObserver to detect DOM changes
      setupDOMObserver() {
        // Disconnect existing observer if any
        if (this.domObserver) {
          this.domObserver.disconnect();
        }
        
        // Create new MutationObserver
        this.domObserver = new MutationObserver((mutations) => {
          let shouldReinitialize = false;
          
          mutations.forEach((mutation) => {
            // Check if nodes were added or removed
            if (mutation.type === 'childList') {
              // Check if any added nodes are product bundle elements
              mutation.addedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  if (node.hasAttribute && node.hasAttribute('data-select-id')) {
                    shouldReinitialize = true;
                    console.log('🔄 New bundle element detected:', node);
                  } else if (node.querySelector && node.querySelector('[data-select-id]')) {
                    shouldReinitialize = true;
                    console.log('🔄 Container with bundle elements detected:', node);
                  }
                }
              });
              
              // Check if any removed nodes were product bundle elements
              mutation.removedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  if (node.hasAttribute && node.hasAttribute('data-select-id')) {
                    shouldReinitialize = true;
                    console.log('🗑️ Bundle element removed:', node);
                  } else if (node.querySelector && node.querySelector('[data-select-id]')) {
                    shouldReinitialize = true;
                    console.log('🗑️ Container with bundle elements removed:', node);
                  }
                }
              });
            }
            
            // Check for attribute changes that might affect data-select-id
            if (mutation.type === 'attributes') {
              if (mutation.attributeName === 'data-select-id' || 
                  mutation.attributeName === 'data-block-id') {
                shouldReinitialize = true;
                console.log('🏷️ Bundle element attributes changed:', mutation.target);
              }
            }
          });
          
          // Reinitialize if needed
          if (shouldReinitialize) {
            console.log('🔄 DOM changes detected, reinitializing...');
            
            // Small delay to ensure DOM is fully updated
            setTimeout(() => {
              this.reinitializeAfterDOMChange();
            }, 100);
          }
        });
        
        // Start observing
        this.domObserver.observe(this, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['data-select-id', 'data-block-id']
        });
        
        console.log('👁️ DOM observer started');
      }
      
      // Reinitialize after DOM changes
      reinitializeAfterDOMChange() {
        try {
          console.log('🔄 Reinitializing after DOM changes...');
          
          // Wait for custom elements to be fully defined and initialized
          setTimeout(() => {
            try {
              // Update item selectors with fresh elements
              this.itemSelector = this.querySelectorAll("product-bundle-variant-item");
              
              // Check if custom elements are properly initialized
              let elementsReady = true;
              this.itemSelector.forEach(item => {
                if (!item.getCurrentVariant || typeof item.getCurrentVariant !== 'function') {
                  elementsReady = false;
                  console.warn('⚠️ Element not fully initialized:', item);
                }
              });
              
              if (!elementsReady) {
                console.log('⏳ Elements not ready, waiting longer...');
                setTimeout(() => this.reinitializeAfterDOMChange(), 500);
                return;
              }
              
              // Refresh hover listeners
              this.setupHoverListeners();
              
              // Update calculations with error handling
              try {
                this.updateTotalPrice();
              } catch (priceError) {
                console.warn('⚠️ Price update error:', priceError);
              }
              
              try {
                this.checkBundleAvailability();
              } catch (availabilityError) {
                console.warn('⚠️ Availability check error:', availabilityError);
              }
              
              try {
                this.checkEmptyBundle();
              } catch (emptyError) {
                console.warn('⚠️ Empty bundle check error:', emptyError);
              }
              
              console.log('✅ Reinitialization completed successfully');
              console.log('📊 Current bundle items:', this.itemSelector.length);
              console.log('📊 Current hoverable elements:', this.productBundleItemSelector.length);
              
            } catch (innerError) {
              console.error('❌ Error during delayed reinitialization:', innerError);
            }
          }, 200); // Initial delay for DOM to settle
          
        } catch (error) {
          console.error('❌ Error during reinitialization setup:', error);
        }
      }
      
      // Cleanup method for when element is removed
      disconnectedCallback() {
        if (this.domObserver) {
          this.domObserver.disconnect();
        }
        
        if (this.themeEditorSelectHandler) {
          document.removeEventListener('shopify:block:select', this.themeEditorSelectHandler);
        }
        
        if (this.themeEditorDeselectHandler) {
          document.removeEventListener('shopify:block:deselect', this.themeEditorDeselectHandler);
        }
      }
      
      // Check if the bundle has any products
      checkEmptyBundle() {
        // Check if there are any product items in the bundle
        const productItems = this.querySelectorAll('product-bundle-variant-item');
        const hasProducts = productItems.length > 0;
        
        if (!hasProducts) {
          // Bundle is empty, disable the add to cart button
          this.disableAddToCartButton('Please add products to this bundle');
          return true; // Bundle is empty
        } else {
          // Count only the products with real products (not placeholders)
          let realProductCount = 0;
          
          productItems.forEach(item => {
            // Get the product ID or check for any identifying attribute that indicates a real product
            const productId = item.getAttribute('data-select-id')?.replace('bundle_block_', '');
            if (productId && productId !== 'undefined' && productId !== 'null') {
              // Check for variant selector OR input field
              const variantSelector = item.querySelector('.product-variant-selector');
              const inputField = item.querySelector('input[name="id"]');
              
              if ((variantSelector && variantSelector.options.length > 0) || inputField) {
                realProductCount++;
              }
            }
          });
          
          if (realProductCount === 0) {
            // No real products in the bundle
            this.disableAddToCartButton('Please add products to this bundle');
            return true; // Bundle is effectively empty
          }
        }
        
        return false; // Bundle has products
      }
      
      // Helper method to disable button with message
      disableAddToCartButton(message) {
        if (this.addToCartButtonElement) {
          this.addToCartButtonElement.classList.add('bundle-unavailable');
          this.addToCartButtonElement.setAttribute('disabled', 'disabled');
          
          // Add or update message
          let unavailableMessage = this.querySelector('.bundle-unavailable-message');
          if (!unavailableMessage) {
            unavailableMessage = document.createElement('div');
            unavailableMessage.className = 'bundle-unavailable-message';
            
            // Find the button area parent to place the message
            const buttonArea = this.querySelector('.bundle-product-button__area');
            if (buttonArea) {
              buttonArea.appendChild(unavailableMessage);
            } else {
              // Fallback to adding after the button if button area not found
              this.addToCartButtonElement.parentNode.insertBefore(unavailableMessage, this.addToCartButtonElement.nextSibling);
            }
          }
          unavailableMessage.textContent = message;
        }
      }
      
      // Show notification message after add to cart
      showNotificationMessage(message, type = 'info', duration = 5000) {
        // Remove any existing notification
        this.removeNotificationMessage();
        
        // Create notification element
        const notification = document.createElement('div');
        notification.className = `bundle-notification bundle-notification--${type}`;
        notification.innerHTML = `
          <div class="bundle-notification__content">
            <span class="bundle-notification__icon">
              ${type === 'warning' ? '⚠️' : type === 'success' ? '✅' : 'ℹ️'}
            </span>
            <span class="bundle-notification__text">${message}</span>
            <button class="bundle-notification__close" type="button">&times;</button>
          </div>
        `;
        
        // Add event listener for close button
        const closeButton = notification.querySelector('.bundle-notification__close');
        closeButton.addEventListener('click', () => {
          this.removeNotificationMessage();
        });
        
        // Find the button area to place the notification
        const buttonArea = this.querySelector('.bundle-product-button__area');
        if (buttonArea) {
          buttonArea.appendChild(notification);
        } else {
          // Fallback to adding after the button
          this.addToCartButtonElement.parentNode.insertBefore(notification, this.addToCartButtonElement.nextSibling);
        }
        
        // Auto-remove after specified duration
        if (duration > 0) {
          setTimeout(() => {
            this.removeNotificationMessage();
          }, duration);
        }
      }
      
      // Remove notification message
      removeNotificationMessage() {
        const existingNotification = this.querySelector('.bundle-notification');
        if (existingNotification) {
          existingNotification.remove();
        }
      }
      
      // Handle variant change event
      handleVariantChange(event) {
        this.updateTotalPrice();
        this.checkBundleAvailability();
      }
      
      // Handle variant image change event
      handleVariantImageChange(event) {
        const { productId, mediaId, mediaSrc, mediaSrcset } = event.detail;
        
        // Find all hotspots related to this product
        const hotspotItems = this.querySelectorAll(`.product-bundle-pin_item[data-select-id="bundle_block_${productId}"]`);
        
        // If no hotspots found, try to update the main bundle image
        if (hotspotItems.length === 0 && this.mainBundleImage) {
          // If it's the main featured product in the bundle, update the main image
          // Check if we have any configuration to determine if we should update the main image
          // This part can be customized based on your theme's specific behavior
          if (this.shouldUpdateMainBundleImage(productId)) {
            this.mainBundleImage.src = mediaSrc;
            if (mediaSrcset) {
              this.mainBundleImage.srcset = mediaSrcset;
            }
          }
        }
      }
      
      // Determine if we should update the main bundle image
      shouldUpdateMainBundleImage(productId) {
        // For example, only update if it's the first product in the bundle
        // or if specific configuration is set
        const firstProduct = this.itemSelector[0];
        if (firstProduct) {
          const firstProductId = firstProduct.getAttribute('data-select-id')?.replace('bundle_block_', '');
          return productId === firstProductId;
        }
        return false;
      }
      
      // Check if all variants in the bundle are available
      checkBundleAvailability() {
        try {
          // First check if bundle is empty
          if (this.checkEmptyBundle()) {
            return; // Already handled empty bundle case
          }
          
          let hasUnavailableVariant = false;
          let unavailableProductTitle = '';
          
          // Check each product item for unavailable variants
          this.itemSelector.forEach(item => {
            try {
              // Check if item has the required method before calling
              if (item && typeof item.isAvailable === 'function') {
                if (!item.isAvailable()) {
                  hasUnavailableVariant = true;
                  
                  // Try to get the product title to show in the message
                  if (typeof item.getProductTitle === 'function') {
                    const productTitle = item.getProductTitle();
                    unavailableProductTitle = productTitle;
                  }
                }
              } else {
                console.warn('⚠️ Item missing isAvailable method:', item);
              }
            } catch (itemError) {
              console.warn('⚠️ Error checking item availability:', item, itemError);
            }
          });
          
          // Update the add to cart button state
          if (this.addToCartButtonElement) {
            if (hasUnavailableVariant) {
              // Disable the button and add unavailable class
              this.disableAddToCartButton(`${unavailableProductTitle} is currently unavailable in the selected options`);
            } else {
              // Enable the button and remove unavailable class
              this.addToCartButtonElement.classList.remove('bundle-unavailable');
              this.addToCartButtonElement.removeAttribute('disabled');
              
              // Remove unavailable message if exists
              const unavailableMessage = this.querySelector('.bundle-unavailable-message');
              if (unavailableMessage) {
                unavailableMessage.remove();
              }
            }
          }
        } catch (error) {
          console.error('❌ Error in checkBundleAvailability:', error);
        }
      }
      
      handleBlockHover(type, event) {
        if (type === "enter") {
          this.classList.add("is-hover");
          this.toggleBlockActive(event.target, true);
        } else {
          this.classList.remove("is-hover");
          this.toggleBlockActive(null, false);
        }
      }
      
      toggleBlockActive(target, isActive) {
        // Always get fresh elements
        const currentElements = this.querySelectorAll("[data-select-id]");
        
        currentElements.forEach((block) => {
          if (target) {
            block.dataset.selectId === target.dataset.selectId
              ? block.classList.add("active")
              : block.classList.remove("active");
          } else {
            block.classList.remove("active");
          }
        });
      }
      
      // Calculate and update total price based on selected variants
      updateTotalPrice() {
        let totalPrice = 0;
        let totalComparePrice = 0;
        
        try {
          this.itemSelector.forEach(item => {
            try {
              // Check if item has the required method before calling
              if (item && typeof item.getCurrentVariant === 'function') {
                const selectedVariant = item.getCurrentVariant();
                if (selectedVariant) {
                  // Parse price strings correctly
                  const priceStr = selectedVariant.dataset.regularPrice || selectedVariant.dataset.price || "0";
                  const price = typeof priceStr === 'string' && priceStr.includes('.') ? 
                    parseFloat(priceStr) : parseInt(priceStr, 10);
                  
                  if (!isNaN(price)) {
                    totalPrice += price;
                  }
                  
                  // Compare price handling
                  if (selectedVariant.dataset.compareAtPrice) {
                    const comparePriceStr = selectedVariant.dataset.compareAtPrice.replace(/[^0-9.-]+/g, "");
                    const comparePrice = parseFloat(comparePriceStr);
                    totalComparePrice += !isNaN(comparePrice) ? comparePrice : price;
                  } else {
                    totalComparePrice += price;
                  }
                }
              } else {
                console.warn('⚠️ Item missing getCurrentVariant method:', item);
              }
            } catch (itemError) {
              console.warn('⚠️ Error processing item:', item, itemError);
            }
          });
          
          // Update the total price display
          if (this.bundleTotalPrice) {
            this.bundleTotalPrice.dataset.price = totalPrice;
            this.bundleTotalPrice.dataset.cpprice = totalComparePrice;
            
            const priceElement = this.bundleTotalPrice.querySelector('.bundle-total-price-money');
            if (priceElement) {
              // Format the price according to store currency formatting
              priceElement.textContent = this.formatMoney(totalPrice);
            }
            
            // Check if we need to show a sale badge/message on the bundle total
            if (totalComparePrice > totalPrice) {
              this.bundleTotalPrice.classList.add('has-discount');
              
              // Calculate discount percentage
              const discountPercent = Math.round((totalComparePrice - totalPrice) / totalComparePrice * 100);
              
              // Update discount display if it exists
              const discountElement = this.bundleTotalPrice.querySelector('.bundle-discount-percent');
              if (discountElement) {
                discountElement.textContent = discountPercent + '%';
              }
            } else {
              this.bundleTotalPrice.classList.remove('has-discount');
            }
          }
        } catch (error) {
          console.error('❌ Error in updateTotalPrice:', error);
        }
      }
      
      // Format money according to store settings (simple implementation)
      formatMoney(cents) {
        if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
          return Shopify.formatMoney(cents);
        }
        
        // Fallback basic formatter
        return '$' + (cents / 100).toFixed(2);
      }
      
      // Handle button click - improved availability checking
      async handleButtonClick(event) {
        event.preventDefault();
        const submitButton = this.querySelector('[type="submit"]');
        
        // Remove any existing notifications
        this.removeNotificationMessage();
        
        submitButton.setAttribute("disabled", true);
        submitButton.classList.add("loading");
        
        // Collect variant information including availability
        const availableVariantIds = [];
        const unavailableVariants = [];
        
        this.itemSelector.forEach(item => {
          const variantId = item.getVariantId();
          const isAvailable = item.isAvailable();
          const productTitle = item.getProductTitle();
          
          if (variantId) {
            if (isAvailable) {
              availableVariantIds.push(variantId);
            } else {
              unavailableVariants.push({
                id: variantId,
                title: productTitle
              });
            }
          }
        });
        
        // Show notification only if there are unavailable variants
        if (unavailableVariants.length > 0) {
          const unavailableProductNames = unavailableVariants.map(variant => variant.title).join(', ');
          
          if (availableVariantIds.length > 0) {
            // Some variants are available, some are not
            this.showNotificationMessage(
              `Some products are currently unavailable and were not added to cart: ${unavailableProductNames}. Available products have been added successfully.`, 
              'warning', 
              8000
            );
          } else {
            // All variants are unavailable - don't proceed with cart add
            this.showNotificationMessage(
              `All selected products are currently unavailable: ${unavailableProductNames}. Please select different variants or try again later.`, 
              'warning', 
              8000
            );
            
            submitButton.classList.remove("loading");
            submitButton.removeAttribute("disabled");
            return;
          }
        }
        
        // Only proceed if there are available variants
        if (availableVariantIds.length > 0) {
          await this.addVariantsToCart(availableVariantIds, submitButton, unavailableVariants.length > 0);
        } else {
          // No available variants - already handled above
          submitButton.classList.remove("loading");
          submitButton.removeAttribute("disabled");
        }
      }
      
      // Add only available variants to cart - improved error handling
      async addVariantsToCart(variantIds, submitButton, hasUnavailableItems = false) {
        // If no variants to add, just return
        if (!variantIds || variantIds.length === 0) {
          submitButton.classList.remove("loading");
          submitButton.removeAttribute("disabled");
          return;
        }
        
        // Create items array for cart - only available variants
        const items = {
          items: variantIds.map((id) => ({
            id: id,
            quantity: 1,
          })),
        };
        
        let sectionsToBundle = [];
    
        if (this.cartNotification) {
          this.cartNotification
            .getSectionsToRender()
            .map((section) => sectionsToBundle.push(section.id));
          this.cartNotification.setActiveElement(document.activeElement);
        }
    
        const body = JSON.stringify({
          ...items,
          sections: sectionsToBundle,
          section_url: window.location.pathname,
        });
    
        try {
          const response = await fetch(`${routes.cart_add_url}?section_id=cart-notification-product`, {
            ...fetchConfig("javascript"),
            body,
          });
          
          const parsedState = await response.json();
          
          // Check for different types of errors
          if (parsedState.status) {
            console.log('Cart add error:', parsedState.description);
            
            // Only show generic error if there were no unavailable items notification already shown
            if (!hasUnavailableItems) {
              this.showNotificationMessage(
                //'There was an issue adding products to cart. Please try again.', 
                'Some selected variants are out of stock or unavailable. Please select different options.',
                'warning', 
                5000
              );
            }
            return;
          }
          
          if (parsedState.status === 422) {
            console.log("422 - Product variant issue");
            
            // 422 usually means variant is out of stock - only show if no unavailable notification already shown
            if (!hasUnavailableItems) {
              this.showNotificationMessage(
                'Some selected variants are out of stock or unavailable. Please select different options.', 
                'warning', 
                5000
              );
            }
          } else {
            // Success - products added to cart
            if (this.cartNotification) {
              this.cartNotification.renderContents(parsedState);
            }
            if (this.cartItems) {
              this.cartItems.renderContents(parsedState);
            }
            
            // No success notification - silent success for available items
          }
        } catch (error) {
          console.error('Cart add error:', error);
          
          // Only show network error if no unavailable notification was already shown
          if (!hasUnavailableItems) {
            this.showNotificationMessage(
              'Network error occurred. Please check your connection and try again.', 
              'warning', 
              5000
            );
          }
        } finally {
          submitButton.classList.remove("loading");
          submitButton.removeAttribute("disabled");
        }
      }
    }
    
    // Define the custom element
    customElements.define("products-bundle-group", ProductBundle);
  }
})(); // End of IIFE